/**
 * weighLabelEscPos.ts — Builder ESC/POS de la etiqueta de pesaje (caja).
 *
 * QUÉ HACE:
 *  - Genera los bytes ESC/POS de la etiqueta pesada: negocio, producto,
 *    precio/kg, peso, total y los códigos escaneables (CODE128 + QR).
 *  - El CODE128 se imprime con `GS k 73` (subconjunto B) y el QR con el
 *    comando NATIVO `GS ( k` (modelo 2) — sin depender del DOM/canvas, con
 *    tamaño de módulo grande y legible en cualquier térmica Epson-compatible.
 *
 * QUÉ EXPORTA: buildWeighLabelEscPosAsync (async), WeighLabelEscPosInput.
 *
 * QUÉ NO DEBE HACER:
 *  - No conoce de storage ni de UI; solo recibe un DTO y devuelve bytes.
 *  - No calcula precios (llegan listos en el input).
 */
import {
  sanitizeForThermal,
  getTicketMaxWidth,
  loadBusinessForReceipt,
} from "@/features/caja/services/ticketRender.service";
import {
  buildCode128EscPosBytes,
  buildQrCodeEscPos,
} from "@/lib/escPosBarcode";
import { fmtCopSymbol } from "../utils/cajaDisplay";

const ESC = 0x1b;
const GS = 0x1d;

export interface WeighLabelEscPosInput {
  /** Nombre del producto (se imprime destacado). */
  productName: string;
  /** Precio por kg (pesos). */
  pricePerKilo: number;
  /** Peso en kg (3 decimales). */
  pesoKg: number;
  /** Total de la línea en pesos. */
  precioTotal: number;
  /** Código CODE128 ya codificado (weighBarcode.encodeWeighLabelCode128). */
  code128: string;
  /** Texto QR ya codificado (weighBarcode.encodeWeighLabelQr) — opcional. */
  qrText?: string;
  /** Etiqueta de unidad para el peso (por defecto "kg"). */
  unitLabel?: string;
  paperWidthMm?: 58 | 80;
}

const joinChunks = (chunks: Uint8Array[]): Uint8Array => {
  const total = chunks.reduce((acc, c) => acc + c.length, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
};

/**
 * Construye la etiqueta de pesaje (negocio, producto, precio/kg, peso, total,
 * CODE128 y QR nativos de la térmica — sin nodos DOM ocultos).
 */
export async function buildWeighLabelEscPosAsync(
  input: WeighLabelEscPosInput,
): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const paper = input.paperWidthMm === 80 ? 80 : 58;
  const maxWidth = getTicketMaxWidth(paper);

  const add = (text: string) => {
    chunks.push(encoder.encode(sanitizeForThermal(text) + "\n"));
  };
  const addRaw = (bytes: number[]) => {
    chunks.push(new Uint8Array(bytes));
  };

  // 1. Inicializar
  addRaw([ESC, 0x40]);
  addRaw([ESC, 0x61, 0x01]); // Centro

  // 2. Negocio (negrita estándar — sin fuentes reducidas que generan glifos raros)
  const biz = loadBusinessForReceipt();
  if (biz.nombre) {
    addRaw([ESC, 0x45, 0x01]); // ESC E — negrita
    add(biz.nombre.toUpperCase());
    addRaw([ESC, 0x45, 0x00]);
    addRaw([0x0a]);
  }

  // 3. Producto — DOBLE ancho/alto + negrita (mismo tamaño que la etiqueta de
  //    producto de Inventario). Los glifos corruptos de antes eran por los
  //    acentos UTF-8, que sanitizeForThermal ya limpia a ASCII.
  addRaw([ESC, 0x21, 0x30]);
  add(
    input.productName
      .substring(0, Math.max(1, Math.floor(maxWidth / 2)))
      .toUpperCase(),
  );
  addRaw([ESC, 0x21, 0x00]);

  addRaw([ESC, 0x61, 0x00]); // Izquierda

  // 4. Precio por kg (fmtCopSymbol = "$" + miles ASCII, sin espacios raros)
  addRaw([ESC, 0x45, 0x01]); // Negrita
  add(
    `PRECIO /${input.unitLabel || "KG"}: ${fmtCopSymbol(Math.round(input.pricePerKilo))}`,
  );

  // 5. Peso
  add(
    `PESO: ${input.pesoKg.toFixed(3)} ${(input.unitLabel || "kg").toUpperCase()}`,
  );

  // 6. Total (destacado)
  add(`TOTAL: ${fmtCopSymbol(Math.round(input.precioTotal))}`);
  addRaw([ESC, 0x45, 0x00]);

  // 7. Código de barras CODE128 en subconjunto C (solo dígitos, la mitad del
  //    ancho del subconjunto B — como las básculas comerciales) con el ancho
  //    de barra calculado para usar todo el papel: sale grande y legible.
  if (input.code128) {
    const digits = input.code128.length;
    const modules = Math.ceil(digits / 2) * 11 + 37;
    const dots = paper === 80 ? 552 : 356;
    const barWidth = Math.max(1, Math.min(4, Math.floor(dots / modules)));
    addRaw([ESC, 0x61, 0x01]); // Centro
    chunks.push(
      buildCode128EscPosBytes(input.code128, {
        height: 64,
        barWidth,
        subset: "C",
      }),
    );
    addRaw([ESC, 0x61, 0x00]);
  }

  // 8. QR nativo (GS ( k, modelo 2) — grande y sin depender del DOM
  if (input.qrText) {
    addRaw([ESC, 0x61, 0x01]);
    addRaw([0x0a]);
    chunks.push(buildQrCodeEscPos(input.qrText, { moduleSize: 6 }));
    addRaw([ESC, 0x61, 0x00]);
  }

  // 9. Espacio final y CORTE automático (GS V 65 n — full cut con feed,
  //    mismo comando estándar del ticket de venta; el valor 42 no corta en
  //    muchas térmicas)
  addRaw([0x0a, 0x0a, 0x0a]);
  addRaw([GS, 0x56, 65, 0x10]);

  return joinChunks(chunks);
}
